import { GripVertical, Plus, Radio } from 'lucide-react'
import { Button, Select } from '../ui'
import { paletteDragStart } from './FloorPlanPalette'

type Props = {
  floorId: string
  addApName: string
  placedApCount: number
  inventoryAps: { id: string; name: string }[]
  placedAps: { id: string; ap: string }[]
  selectedApId: string | null
  onAddApName: (name: string) => void
  onAddAp: () => void
  onSelectAp: (id: string) => void
}

export default function FloorPlanApPanel({
  floorId,
  addApName,
  placedApCount,
  inventoryAps,
  placedAps,
  selectedApId,
  onAddApName,
  onAddAp,
  onSelectAp,
}: Props) {
  const onFloorNames = new Set(placedAps.map(p => p.ap))
  const available = inventoryAps.filter(d => !onFloorNames.has(d.name))

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-mg-border bg-mg-page p-2.5">
        <p className="text-xs font-semibold text-mg-text">Quick add</p>
        <div className="mt-2 flex gap-2">
          <Select value={addApName} className="min-w-0 flex-1" onChange={e => onAddApName(e.target.value)}>
            <option value="">Inventory AP…</option>
            {inventoryAps.map(d => (
              <option key={d.id} value={d.name}>
                {d.name}
              </option>
            ))}
          </Select>
          <Button variant="secondary" size="sm" disabled={!addApName || !floorId} onClick={onAddAp}>
            <Plus size={14} />
          </Button>
        </div>
      </div>

      {available.length > 0 && (
        <div>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-mg-muted">
            Drag onto map · {available.length} available
          </p>
          <ul className="max-h-40 space-y-1 overflow-y-auto">
            {available.map(ap => (
              <li key={ap.id}>
                <div
                  draggable
                  title={`Drag ${ap.name} onto the floor map`}
                  className="floor-ap-drag-item"
                  onDragStart={e => paletteDragStart(e, { kind: 'ap', apName: ap.name, deviceId: ap.id })}
                >
                  <GripVertical size={14} className="shrink-0 text-mg-muted" />
                  <Radio size={12} className="shrink-0 text-mg-accent" />
                  <span className="truncate text-xs font-medium">{ap.name}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-mg-muted">
          On floor · {placedApCount}
        </p>
        {placedAps.length === 0 ? (
          <p className="rounded-lg border border-dashed border-mg-border px-3 py-4 text-center text-[11px] text-mg-muted">
            No APs yet — drag from inventory above or use Quick add.
          </p>
        ) : (
          <ul className="max-h-[min(320px,40vh)] space-y-1 overflow-y-auto">
            {placedAps.map(ap => (
              <li key={ap.id}>
                <button
                  type="button"
                  onClick={() => onSelectAp(ap.id)}
                  className={`flex w-full items-center gap-2 rounded-lg border px-2 py-1.5 text-left transition-colors ${
                    selectedApId === ap.id
                      ? 'border-mg-accent/40 bg-mg-accent-soft text-mg-accent'
                      : 'border-mg-border bg-mg-surface text-mg-text hover:border-mg-accent/25'
                  }`}
                >
                  <span className="floor-ap-dot floor-ap-dot--medium shrink-0" />
                  <span className="truncate text-xs font-medium">{ap.ap}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
